import { useState } from 'react'
import { useBahasa } from '../../../app/i18n'
import { useOrangSaya, useTerbitkanKode } from '../../../domains/tenancy/api'
import { useCari } from '../../../shared/lib/cari'
import { istilah } from '../../../shared/lib/istilah'
import { tanggal } from '../../../shared/lib/waktu'
import {
  Baris, Bendera, BilahTabel, Chip, Gagal, KepalaHalaman, Kosong, Memuat, Panel, Seg,
  Tabel, Tombol,
} from '../../../shared/ui'

/**
 * TIM — orang, peran, penautan Telegram, dan kode `/join`.
 *
 * YANG DILAKUKAN ADMIN DI SINI CUMA SATU: menautkan akun Telegram orangnya. Penambahan
 * orang dilakukan Smova, dan itu disengaja — roster menentukan siapa yang bisa ditugaskan
 * meeting dan siapa yang menerima notifikasi, jadi ia data induk.
 *
 * Versi sebelumnya DIBACA SAJA, dengan catatan bahwa membangun formulirnya "tanpa
 * menampilkan grupnya lebih dulu akan mengundang salah tautan". Itu benar untuk MENAMBAH
 * ORANG — dan tidak berlaku untuk menerbitkan kode, yang tidak membuat siapa pun: ia
 * memberi orang yang SUDAH ada di roster satu kode sekali-pakai.
 *
 * Akibat dari tidak adanya tombol itu nyata: orang yang belum menjalankan `/join` tidak
 * menerima notifikasi apa pun, tombol Telegram menolaknya, dan admin tidak punya satu pun
 * cara memperbaikinya dari layar.
 *
 * KODE MILIK SATU ORANG DAN SEKALI PAKAI (ADR-007). Bukan satu kode bersama: kode bersama
 * berarti siapa pun yang melihatnya bisa menautkan dirinya sebagai anggota tim.
 */
export default function Tim() {
  const { t, locale } = useBahasa()
  const q = useOrangSaya()
  const terbit = useTerbitkanKode()
  const [saring, setSaring] = useState('semua')
  // Kode yang baru diterbitkan, per orang — DI ATAS yang datang dari daftar.
  //
  // Sejak `/dashboard/web-users` mengembalikan undangan yang masih berlaku, kode tidak
  // lagi hilang saat pindah halaman. Peta lokal ini tetap ada untuk satu hal: jawaban
  // POST sampai lebih dulu daripada daftar yang dimuat ulang, jadi tanpa ia kodenya
  // berkedip hilang sebentar tepat sesudah tombol ditekan.
  const [kode, setKode] = useState({})

  const { cari, setCari, hasil } = useCari(
    q.data, (u) => [u.username, u.role, u.telegram_user_id])

  if (q.isLoading) return <Memuat tinggi={5} />
  if (q.isError) return <Gagal galat={q.error} ulangi={q.refetch} />

  const baris = q.data ?? []
  const tertaut = (u) => Boolean(u.telegram_user_id)
  const belum = baris.filter((u) => !tertaut(u))

  const disaring = hasil.filter((u) => (
    saring === 'semua' ? true : saring === 'belum' ? !tertaut(u) : tertaut(u)
  ))

  return (
    <>
      <KepalaHalaman judul={t('ta.navTim')} keterangan={t('ta.timCatatan')} />

      {/* Spanduk ini yang membuat layar berguna dibuka. Tanpa penautan, notifikasi tetap
          TERBACA semua orang di grup — yang berhenti bekerja tombolnya, dan itu tidak
          terlihat sampai ada yang menekannya lalu ditolak. */}
      {belum.length > 0 && (
        <Bendera nada="warn" className="max-w-[78ch]">
          {t('ta.timBelumTertaut', { n: belum.length })}
        </Bendera>
      )}

      <Panel judul={t('ta.timHitung', { n: baris.length })} catatan={t('ta.timSiapa')}>
      {baris.length === 0 ? (
        <Kosong judul={t('ta.timKosong')} />
      ) : (
        <>
        <BilahTabel
          cari={{ nilai: cari, set: setCari, placeholder: t('umum.cari') }}
          ringkas={t('umum.dari', {
            mulai: disaring.length ? 1 : 0, akhir: disaring.length, total: baris.length,
          })}
        >
          <Seg
            nilai={saring}
            set={setSaring}
            daftar={[
              { id: 'semua', label: t('ta.saringSemua') },
              { id: 'belum', label: t('ta.saringBelum') },
              { id: 'sudah', label: t('ta.saringSudah') },
            ]}
          />
        </BilahTabel>

        <Tabel kepala={[t('ta.kolNama'), t('ta.kolPeran'), t('ta.kolTelegram'),
                        t('ta.kolKode'), t('ta.kolBolehDitugaskan'),
                        t('ta.kolAkunAktif')]}>
          {disaring.map((u) => {
            // Yang baru diterbitkan menang atas yang datang dari daftar — keduanya kode
            // yang sama kecuali pada detik-detik antara POST dan pemuatan ulang.
            const k = kode[u.id]
              ?? (u.kode ? { code: u.kode, expires_at: u.kode_kedaluwarsa,
                             sudah_ada: true } : null)
            return (
              <Baris key={u.id}>
                <td className={u.is_active ? 'font-semibold' : 'font-semibold text-ink3'}>
                  {u.username}
                </td>
                <td className="text-[12px] text-ink2">{istilah(t, 'peran', u.role)}</td>
                <td className="font-mono text-[11.5px] text-ink2">
                  {u.telegram_user_id
                    || <span className="text-ink3">{t('ta.belumTertaut')}</span>}
                </td>
                <td>
                  {tertaut(u) ? (
                    <span className="text-[11.5px] text-ink3">—</span>
                  ) : k ? (
                    <span className="flex flex-col gap-0.5">
                      {/* `select-all` supaya satu klik memilih seluruh kodenya. Kode yang
                          tersalin separuh gagal dengan pesan "kode tidak dikenal", jauh
                          dari sebabnya. */}
                      <b className="font-mono text-[12.5px] text-accent select-all">
                        {k.code}
                      </b>
                      <span className="text-[10.5px] text-ink3">
                        {t(k.sudah_ada ? 'ta.kodeLama' : 'ta.kodeBaru', {
                          sampai: tanggal(k.expires_at, locale),
                        })}
                      </span>
                    </span>
                  ) : (
                    <Tombol
                      kecil
                      jenis="sekunder"
                      disabled={terbit.isPending}
                      onClick={() => terbit.mutate(u.id, {
                        onSuccess: (d) => setKode((x) => ({ ...x, [u.id]: d })),
                      })}
                    >
                      {t('ta.terbitkanKode')}
                    </Tombol>
                  )}
                </td>
                {/* BOLEH DITUGASKAN — dibaca saja di sini, diubah di Meeting →
                    Ketersediaan. Rancangan menaruh kolom ini di kedua layar, dan itu
                    benar: pertanyaan "kenapa nama ini tidak pernah ditawarkan bot"
                    muncul saat orang melihat daftar TIMNYA, bukan saat ia kebetulan
                    membuka tab ketersediaan di layar meeting.

                    Tidak dibuat tombol supaya satu nilai tidak punya dua tempat
                    mengubah; yang kedua selalu jadi yang lupa diperbarui. */}
                <td>
                  <Chip nada={u.boleh_ditugaskan ? 'ok' : 'idle'}>
                    {u.boleh_ditugaskan ? t('ta.bolehYa') : t('ta.bolehTidak')}
                  </Chip>
                </td>
                <td>
                  <Chip nada={u.is_active ? 'ok' : 'idle'}>
                    {u.is_active ? t('adm.aktif') : t('adm.nonaktif')}
                  </Chip>
                </td>
              </Baris>
            )
          })}
        </Tabel>
        </>
      )}

      {terbit.isError && (
        <div className="p-3.5 border-t border-line">
          <Bendera nada="bad" role="alert">{terbit.error?.message}</Bendera>
        </div>
      )}
      </Panel>

      <Bendera nada="info" judul={t('ta.kodeSatuOrangJudul')} className="max-w-[78ch]">
        {t('ta.kodeSatuOrangIsi')}
      </Bendera>
    </>
  )
}
